import { createContext, useContext, useEffect, useState } from "react";
import { TYPE } from "../constants/typography";

// ─── Loading placeholders, with a time limit ────────────────────────────────
// Sprint 14, 3.2 (David, 06/10/2026). A grey block that never goes away was
// reported more than once (home, market intelligence, practice account, in
// day mode). Whatever call it is waiting on, a placeholder must not outlive
// it: after LOAD_TIMEOUT_MS with no data it gives way to a line that says so.
//
// The one way to draw a placeholder in this app is here:
//   <Skeleton/>              one block. On its own it keeps its own clock.
//   <Loading>…</Loading>     a group of blocks that time out together, so a
//                            card of three lines shows one message, not three.
// A test (Skeleton.test.js) fails when a screen draws a placeholder of its
// own · the class "si-skel" or a local Skel helper outside this file.
export const LOAD_TIMEOUT_MS = 8000;
export const LOAD_TIMEOUT_TEXT = "הנתונים לא נטענו · נסה לרענן";

const InGroup = createContext(false);

function useExpired(ms) {
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setExpired(true), ms);
    return () => clearTimeout(t);
  }, [ms]);
  return expired;
}

export function LoadTimeout({ style }) {
  return <div role="status" style={{ ...TYPE.caption, padding: "6px 0", ...style }}>{LOAD_TIMEOUT_TEXT}</div>;
}

function Block({ w = "100%", h = 14, mb = 0, radius, style, label }) {
  return (
    <div className="si-skel" aria-label={label}
      style={{ width: w, height: h, marginBottom: mb, ...(radius != null ? { borderRadius: radius } : null), ...style }}/>
  );
}

function Lone(props) {
  const expired = useExpired(props.ms ?? LOAD_TIMEOUT_MS);
  return expired ? <LoadTimeout/> : <Block {...props}/>;
}

/** One placeholder block. Times out by itself unless inside <Loading>. */
export function Skeleton(props) {
  const grouped = useContext(InGroup);
  return grouped ? <Block {...props}/> : <Lone {...props}/>;
}

/** A group of placeholders that gives way to one message after the limit. */
export function Loading({ children, ms = LOAD_TIMEOUT_MS, style }) {
  const expired = useExpired(ms);
  if (expired) return <LoadTimeout style={style}/>;
  return <InGroup.Provider value={true}><div aria-busy="true" style={style || { display: "contents" }}>{children}</div></InGroup.Provider>;
}
